import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { signInEmailPassword, signInWithGoogle, signInWithMicrosoft, isFirebaseEnabled, resetPassword } from '@/auth/firebase';

const SITE_URL = (import.meta.env.VITE_SITE_URL ?? (import.meta.env.DEV ? 'http://localhost:5181' : '')).replace(/\/$/, '');

export default function SignInPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/app/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'sign-in' | 'forgot'>('sign-in');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      if (mode === 'forgot') {
        await resetPassword(email);
        alert('Password reset email sent.');
        setMode('sign-in');
      } else {
        await signInEmailPassword(email, password);
        nav(next, { replace: true });
      }
    } catch (e: any) {
      setErr(e?.message ?? 'Sign-in failed');
    } finally { setBusy(false); }
  }

  async function social(p: 'google' | 'microsoft') {
    setErr(null);
    try {
      if (p === 'google') await signInWithGoogle(); else await signInWithMicrosoft();
      nav(next, { replace: true });
    } catch (e: any) {
      setErr(e?.message ?? 'Sign-in failed');
    }
  }

  return (
    <AuthShell title={mode === 'forgot' ? 'Reset password' : 'Welcome back'}>
      {!isFirebaseEnabled && (
        <div className="mb-4 p-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded">
          Firebase is not configured. Demo mode — any credentials let you in.
        </div>
      )}

      {mode === 'sign-in' && (
        <>
          <div className="space-y-2 mb-5">
            <button onClick={() => social('google')} className="w-full px-4 py-2.5 border border-stone-300 rounded-lg hover:bg-stone-50 text-sm font-medium flex items-center justify-center gap-2">
              <span className="font-bold text-base">G</span> Continue with Google
            </button>
            <button onClick={() => social('microsoft')} className="w-full px-4 py-2.5 border border-stone-300 rounded-lg hover:bg-stone-50 text-sm font-medium flex items-center justify-center gap-2">
              <span className="font-bold text-base">⊞</span> Continue with Microsoft
            </button>
          </div>
          <div className="flex items-center gap-3 my-5 text-xs text-stone-400 uppercase tracking-wider">
            <span className="flex-1 h-px bg-stone-200" /> or <span className="flex-1 h-px bg-stone-200" />
          </div>
        </>
      )}

      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </div>
        {mode === 'sign-in' && (
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Password</label>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
        )}
        {err && <p className="text-xs text-red-600">{err}</p>}
        <button type="submit" disabled={busy} className="w-full px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white rounded-lg text-sm font-medium">
          {busy ? '…' : (mode === 'forgot' ? 'Send reset email' : 'Sign in')}
        </button>
      </form>

      <div className="mt-6 text-center text-xs text-stone-500 space-y-2">
        {mode === 'sign-in' ? (
          <>
            <button onClick={() => setMode('forgot')} className="hover:text-stone-700">Forgot password?</button>
            <p>No account? <Link to="/auth/sign-up" className="text-violet-700 font-medium hover:underline">Start free trial</Link></p>
          </>
        ) : (
          <button onClick={() => setMode('sign-in')} className="hover:text-stone-700">← Back to sign in</button>
        )}
      </div>
    </AuthShell>
  );
}

function AuthShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-violet-50 flex flex-col">
      <header className="px-6 py-5">
        <a href={SITE_URL} className="flex items-center gap-2 text-stone-700 hover:text-stone-900">
          <span className="w-7 h-7 bg-violet-600 rounded-md flex items-center justify-center text-white text-sm">◈</span>
          <span className="font-semibold">ContentHub</span>
        </a>
      </header>
      <main className="flex-1 flex items-start justify-center px-6 pt-12">
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-semibold text-stone-900 text-center mb-6">{title}</h1>
          <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">{children}</div>
        </div>
      </main>
    </div>
  );
}
